import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth, useUser } from "@clerk/react";
import { Sentry } from "@/lib/sentry";

export type LifecycleState =
  | "active"
  | "trial"
  | "trial_ending"
  | "past_due_grace"
  | "cancel_scheduled"
  | "read_only"
  | "cancelled";

export interface Lifecycle {
  state: LifecycleState;
  isReadOnly: boolean;
  trialDaysLeft: number | null;
  gracePeriodEndsAt: string | null;
  cancelAt: string | null;
  trialEndsAt: string | null;
  message: string;
  cta: { label: string; action: "billing_portal" | "checkout" } | null;
}

// "AD" = Athletic Director (top exec; currently inert behaviorally).
// "SA" = Sport Administrator (mid-level role that supervises coaches by
// sport and signs off on coach contracts in their mapped sports).
// See lib/db PERSONA_VALUES for the persisted enum and PersonaBadge for
// user-facing labels.
export type Persona = "Finance" | "HR" | "Legal" | "AD" | "SA";

export interface Org {
  id: number;
  clerkUserId: string | null;
  name: string;
  institutionName: string | null;
  division: string | null;
  conference: string | null;
  plan: "free" | "track" | "draft" | "enterprise";
  purchasedSeats: number;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  stripeCurrentPeriodEnd: string | null;
  stripePaymentBrand: string | null;
  stripePaymentLast4: string | null;
  emailAlertsEnabled: boolean;
  approvalNotificationsEnabled: boolean;
  allowedSigningMethods: "external_only" | "in_app_only" | "both";
  alertEmail: string | null;
  role: "admin" | "viewer" | "coach";
  personas: Persona[];
  lastActivePersona: Persona | null;
  logoUrl: string | null;
  primaryColor: string | null;
  coachCount?: number;
  createdAt: string;
  updatedAt: string;
  lifecycle: Lifecycle;
  isDemo?: boolean;
  /**
   * Task #448: when the visitor redeemed an Enterprise-Preview invitation
   * the API stamps `enterprise` here so the React shell can flip the banner
   * variant and components that key off `isDemo` can lift list caps.
   */
  demoTier?: "standard" | "enterprise" | null;
  // Server-stamped UTC time the demo session began. Only set for demo orgs;
  // the client uses it to drive the 15-minute timeout / 13-minute warning.
  demoSessionStartedAt?: string | null;
}

interface OrgContextValue {
  org: Org | null;
  loading: boolean;
  error: "not_found" | "unauthorized" | "error" | null;
  refetch: () => void;
}

const OrgContext = createContext<OrgContextValue>({
  org: null,
  loading: true,
  error: null,
  refetch: () => {},
});

export function OrgProvider({ children }: { children: React.ReactNode }) {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  const [org, setOrg] = useState<Org | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<OrgContextValue["error"]>(null);

  const fetchOrg = useCallback(async () => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      setOrg(null);
      setLoading(false);
      setError("unauthorized");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch("/api/auth/org", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 404) {
        setOrg(null);
        setError("not_found");
      } else if (res.status === 401) {
        setOrg(null);
        setError("unauthorized");
      } else if (!res.ok) {
        setOrg(null);
        setError("error");
      } else {
        const data = await res.json();
        const lifecycle: Lifecycle = data.lifecycle ?? {
          state: "active",
          isReadOnly: false,
          trialDaysLeft: null,
          gracePeriodEndsAt: null,
          cancelAt: null,
          trialEndsAt: null,
          message: "",
          cta: null,
        };
        setOrg({ ...data, role: data.role ?? "admin", personas: data.personas ?? [], lastActivePersona: data.lastActivePersona ?? null, lifecycle });
        setError(null);
      }
    } catch {
      setOrg(null);
      setError("error");
    } finally {
      setLoading(false);
    }
  }, [isLoaded, isSignedIn, getToken]);

  useEffect(() => {
    fetchOrg();
  }, [fetchOrg]);

  // Tag Sentry events with the current Clerk user and org so captured
  // exceptions are attributable. No-op when Sentry isn't initialized.
  useEffect(() => {
    if (user?.id) {
      Sentry.setUser({ id: user.id });
    } else {
      Sentry.setUser(null);
    }
  }, [user?.id]);
  useEffect(() => {
    Sentry.setTag("org_id", org?.id != null ? String(org.id) : null);
    Sentry.setTag("org_plan", org?.plan ?? null);
  }, [org?.id, org?.plan]);

  return (
    <OrgContext.Provider value={{ org, loading, error, refetch: fetchOrg }}>
      {children}
    </OrgContext.Provider>
  );
}

export function useOrg() {
  return useContext(OrgContext);
}
